import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppNav } from "@/components/AppNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { formatIDR, currentYear, yearOptions, MONTH_ABBR } from "@/lib/month";
import { CalendarRange, Loader2, Plus } from "lucide-react";

function pctTone(p, dark) {
  if (!p) return dark ? "text-slate-500" : "text-slate-400";
  if (p >= 100) return dark ? "text-emerald-400" : "text-emerald-600";
  if (p >= 60) return dark ? "text-amber-400" : "text-amber-600";
  return dark ? "text-rose-400" : "text-rose-600";
}

function short(n) {
  n = Number(n || 0);
  if (n === 0) return "–";
  if (n >= 1e9) return (n / 1e9).toFixed(1) + "B";
  if (n >= 1e6) return (n / 1e6).toFixed(0) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(0) + "k";
  return String(n);
}

// ---- Bulk targets for a whole year (admin) ----
function BulkYearDialog({ year, rows, onSaved }) {
  const [open, setOpen] = useState(false);
  const [vals, setVals] = useState({});
  const [saving, setSaving] = useState(false);
  const upd = (pid, patch) => setVals((p) => ({ ...p, [pid]: { ...(p[pid] || {}), ...patch } }));

  const submit = async () => {
    setSaving(true);
    try {
      const targets = rows.map((r) => ({
        property_id: r.property_id,
        revenue_target: Number(vals[r.property_id]?.revenue_target || 0),
        profit_percent: vals[r.property_id]?.profit_percent === "" || vals[r.property_id]?.profit_percent === undefined ? null : Number(vals[r.property_id].profit_percent),
      }));
      const { data } = await api.post("/targets/bulk-year", { year, targets });
      toast.success(`Saved ${data.saved} monthly targets${data.skipped_locked_months?.length ? ` · skipped locked: ${data.skipped_locked_months.join(", ")}` : ""}`);
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="bulk-year-btn"><Plus className="h-4 w-4" /> Bulk year targets</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk targets — year {year}</DialogTitle>
          <DialogDescription>Set one monthly revenue target (and optional profit margin) per property, applied to all 12 months of {year}. Locked months are skipped automatically.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.property_id} className="grid grid-cols-12 gap-2 items-center" data-testid={`bulk-year-row-${r.property_id}`}>
              <span className="col-span-4 text-sm truncate">{r.property_name}</span>
              <Input className="col-span-5" type="number" min="0" placeholder="Monthly target (IDR)" value={vals[r.property_id]?.revenue_target ?? ""} onChange={(e) => upd(r.property_id, { revenue_target: e.target.value })} data-testid={`bulk-year-target-${r.property_id}`} />
              <Input className="col-span-3" type="number" min="0" placeholder="Profit %" value={vals[r.property_id]?.profit_percent ?? ""} onChange={(e) => upd(r.property_id, { profit_percent: e.target.value })} data-testid={`bulk-year-profit-${r.property_id}`} />
            </div>
          ))}
          {rows.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No properties yet.</p>}
        </div>
        <Button onClick={submit} className="w-full" disabled={saving || rows.length === 0} data-testid="save-bulk-year-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : `Apply to all 12 months of ${year}`}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export default function YearlyPage() {
  const { user } = useAuth();
  const dark = user?.role === "admin";
  const [year, setYear] = useState(currentYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/performance/yearly?year=${year}`);
      setData(data);
      setDraft({});
    } catch { toast.error("Failed to load yearly overview"); }
    finally { setLoading(false); }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const cellDraft = (pid, m, fallback) => draft[`${pid}:${m}`] ?? (fallback || "");
  const setCellDraft = (pid, m, v) => setDraft((p) => ({ ...p, [`${pid}:${m}`]: v }));
  const saveTarget = async (pid, m, v) => {
    try {
      await api.post("/targets", { property_id: pid, month: m, revenue_target: Number(v || 0), profit_percent: null });
      toast.success(`Target ${m} saved`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const border = dark ? "border-slate-800" : "border-slate-200";
  const cardBg = dark ? "bg-slate-900" : "bg-white";

  return (
    <div className={`min-h-screen ${dark ? "bg-[#1F1B16] dark-scroll text-slate-100" : "bg-slate-50"}`}>
      <AppNav />
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
          <div>
            <h1 className={`font-display text-2xl font-bold flex items-center gap-2 ${dark ? "text-white" : "text-slate-900"}`}>
              <CalendarRange className="h-6 w-6 text-indigo-500" /> Yearly Overview
            </h1>
            <p className={`text-sm ${dark ? "text-slate-400" : "text-slate-500"}`}>Monthly target vs final per property · {year}</p>
          </div>
          <div className="flex items-center gap-2">
            {dark && <BulkYearDialog year={year} rows={data?.rows || []} onSaved={load} />}
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className={`w-32 ${dark ? "bg-slate-900 border-slate-700 text-slate-200" : ""}`} data-testid="year-select"><SelectValue /></SelectTrigger>
              <SelectContent>{yearOptions().map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        {loading || !data ? (
          <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-indigo-500" /></div>
        ) : data.rows.length === 0 ? (
          <div className={`rounded-xl border ${border} ${cardBg} p-10 text-center ${dark ? "text-slate-400" : "text-slate-500"}`}>No properties to show.</div>
        ) : (
          <div className={`rounded-xl border ${border} overflow-hidden ${cardBg}`}>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="yearly-table">
                <thead>
                  <tr className={`border-b ${border} ${dark ? "text-slate-400" : "text-slate-500"}`}>
                    <th className={`p-2.5 text-left font-semibold sticky left-0 ${cardBg} min-w-[150px]`}>PROPERTY</th>
                    {MONTH_ABBR.map((m) => <th key={m} className="p-2.5 text-center font-semibold min-w-[70px]">{m}</th>)}
                    <th className="p-2.5 text-center font-semibold min-w-[90px] bg-amber-400/90 text-slate-900">YEAR</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.property_id} className={`border-b ${border} ${dark ? "hover:bg-slate-800/40" : "hover:bg-slate-50"}`} data-testid={`yearly-row-${r.property_id}`}>
                      <td className={`p-2.5 font-medium sticky left-0 ${cardBg} ${dark ? "text-white" : "text-slate-900"}`}>{r.property_name}</td>
                      {r.cells.map((c) => (
                        <td key={c.month} className="p-1.5 text-center align-middle" title={`Target ${formatIDR(c.target)} · Final ${formatIDR(c.final)}`}>
                          <div className={`font-mono font-semibold ${dark ? "text-slate-200" : "text-slate-700"}`}>{short(c.final)}</div>
                          {dark ? (
                            <input
                              type="number" min="0"
                              disabled={(data?.locked_months || []).includes(c.month)}
                              className="w-16 mx-auto block text-center text-[10px] font-mono rounded border px-0.5 py-0.5 bg-slate-800 border-slate-700 text-slate-300 disabled:opacity-40"
                              value={cellDraft(r.property_id, c.month, c.target)}
                              onChange={(e) => setCellDraft(r.property_id, c.month, e.target.value)}
                              onBlur={(e) => { if (Number(e.target.value || 0) !== (c.target || 0)) saveTarget(r.property_id, c.month, e.target.value); }}
                              data-testid={`year-target-${r.property_id}-${c.month}`}
                            />
                          ) : (
                            <div className={`font-mono text-[10px] ${dark ? "text-slate-500" : "text-slate-400"}`}>/{short(c.target)}</div>
                          )}
                          <div className={`text-[10px] font-semibold ${pctTone(c.pct, dark)}`}>{c.target ? `${c.pct}%` : ""}</div>
                        </td>
                      ))}
                      <td className={`p-2 text-center bg-amber-400/10`}>
                        <div className={`font-mono font-semibold ${dark ? "text-white" : "text-slate-900"}`}>{short(r.year_final)}</div>
                        <div className={`font-mono text-[10px] ${dark ? "text-slate-500" : "text-slate-400"}`}>/{short(r.year_target)}</div>
                        <div className={`text-[10px] font-bold ${pctTone(r.year_pct, dark)}`}>{r.year_target ? `${r.year_pct}%` : ""}</div>
                      </td>
                    </tr>
                  ))}
                  <tr className={`${dark ? "bg-slate-800/60" : "bg-slate-100"} font-semibold`} data-testid="yearly-total-row">
                    <td className={`p-2.5 sticky left-0 ${dark ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-900"}`}>TOTAL</td>
                    {data.totals.map((t) => (
                      <td key={t.month} className="p-1.5 text-center">
                        <div className={`font-mono ${dark ? "text-slate-100" : "text-slate-800"}`}>{short(t.final)}</div>
                        <div className={`font-mono text-[10px] ${dark ? "text-slate-500" : "text-slate-400"}`}>/{short(t.target)}</div>
                        <div className={`text-[10px] ${pctTone(t.pct, dark)}`}>{t.target ? `${t.pct}%` : ""}</div>
                      </td>
                    ))}
                    <td className="p-2 text-center bg-amber-400/20">
                      <div className={`font-mono ${dark ? "text-white" : "text-slate-900"}`}>{short(data.year_final)}</div>
                      <div className={`font-mono text-[10px] ${dark ? "text-slate-500" : "text-slate-400"}`}>/{short(data.year_target)}</div>
                      <div className={`text-[10px] font-bold ${pctTone(data.year_pct, dark)}`}>{data.year_target ? `${data.year_pct}%` : ""}</div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className={`text-xs px-3 py-2 ${dark ? "text-slate-500" : "text-slate-400"}`}>Each cell shows final revenue over its monthly target, with achievement %. Hover a cell for full figures.{dark && " As admin, type a new target directly in any cell (auto-saves on blur) — locked months are disabled."}</p>
          </div>
        )}
      </main>
    </div>
  );
}
